// Automatic telemetry is an allowlist of technical fields, never free-text errors.
export const telemetryKinds = [
  "runtime",
  "rejection",
  "react",
  "request",
  "socket",
  "export",
  "import",
] as const;
export const operations = [
  "session",
  "projects",
  "files",
  "sync",
  "review",
  "export",
  "creative",
  "library",
  "agents",
  "account",
  "reports",
  "telemetry",
  "health",
  "operations",
  "other",
] as const;
export type Operation = (typeof operations)[number];
export type TelemetryKind = (typeof telemetryKinds)[number];
const errorTypes = [
  "Error",
  "TypeError",
  "RangeError",
  "ReferenceError",
  "SyntaxError",
  "URIError",
  "EvalError",
  "AbortError",
  "TimeoutError",
  // Standard DOMException names (e.g. browser storage full for the import outbox).
  "QuotaExceededError",
  "NotAllowedError",
  "NotFoundError",
  "NotReadableError",
  "InvalidStateError",
  "DataCloneError",
  "SecurityError",
  "NetworkError",
  "EncodingError",
  "Unknown",
] as const;
export type TelemetryEvent = {
  kind: TelemetryKind;
  operation: Operation;
  build: string;
  errorType: string;
  frames: string[];
  status: number;
  durationMs: number;
  requestId: string;
};
export function operationFor(
  raw: string,
  origin = "http://localhost",
): Operation {
  try {
    const url = new URL(raw, origin);
    if (url.origin !== origin || !url.pathname.startsWith("/api/"))
      return "other";
    const p = url.pathname;
    if (p.startsWith("/api/ops/")) return "operations";
    if (p === "/api/telemetry") return "telemetry";
    if (p === "/api/bug-reports") return "reports";
    if (p === "/api/health") return "health";
    if (/^\/api\/(session|logout|profile)$/.test(p)) return "session";
    if (/^\/api\/rooms\/[^/]+\/files(?:\/|$)/.test(p)) return "files";
    if (/^\/api\/rooms\/[^/]+\/sync$/.test(p)) return "sync";
    if (/^\/api\/rooms\/[^/]+\/export-access$/.test(p)) return "export";
    if (p === "/api/library" || p.startsWith("/api/library/")) return "library";
    if (
      p === "/api/agent-oauth" ||
      p.startsWith("/api/agent-oauth/") ||
      /^\/api\/rooms\/[^/]+\/agent(?:\/|$)/.test(p)
    )
      return "agents";
    if (/^\/api\/(account|admin)(?:\/|$)/.test(p)) return "account";
    if (/^\/api\/rooms\/[^/]+\/creative-(images|videos|sketches)(?:\/|$)/.test(p))
      return "creative";
    if (
      /^\/api\/(updates|actions)(?:\/|$)/.test(p) ||
      /^\/api\/rooms\/[^/]+\/(actions|review-target|people|changes|text-mentions)(?:\/|$)/.test(p)
    )
      return "review";
    // Remaining project routes (create, join, invites, members, archive...).
    if (p === "/api/rooms" || p.startsWith("/api/rooms/")) return "projects";
  } catch {
    /* Invalid URLs have no identifying path in telemetry. */
  }
  return "other";
}
export function safeFrames(stack: unknown): string[] {
  if (typeof stack !== "string") return [];
  // Only compiled JS locations, no message, function name, origin, query or local path.
  return [
    ...stack
      .slice(0, 12_000)
      .matchAll(
        /(?:\/assets\/([\w-]{1,100}\.js)|\b(index\.js|worker\.js)):(\d{1,7}):(\d{1,7})(?=[\s)]|$)/g,
      ),
  ]
    .slice(0, 6)
    .map((m) => `${m[1] ?? m[2]}:${m[3]}:${m[4]}`);
}
export function technicalError(error: unknown) {
  try {
    if (error instanceof Error) {
      const message = typeof error.message === "string" ? error.message : "";
      const stack = typeof error.stack === "string" ? error.stack : "";
      return {
        errorType: errorTypes.includes(
          error.name as (typeof errorTypes)[number],
        )
          ? error.name
          : "Unknown",
        // V8 stacks start with the free-text message; drop it before parsing so
        // message text shaped like a bundle location can never become a frame.
        frames: safeFrames(message ? stack.split(message).join(" ") : stack),
      };
    }
  } catch {
    /* Hostile getters must not break reporting. */
  }
  return { errorType: "Unknown", frames: [] as string[] };
}
export function parseTelemetry(raw: unknown): TelemetryEvent {
  if (!raw || typeof raw !== "object" || Array.isArray(raw))
    throw new Error("Invalid telemetry");
  const r = raw as Record<string, unknown>;
  if (
    !telemetryKinds.includes(r.kind as TelemetryKind) ||
    !operations.includes(r.operation as Operation)
  )
    throw new Error("Invalid telemetry kind");
  const integer = (v: unknown, max: number) =>
    typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= max ? v : 0;
  // Long operations are capped rather than recorded as instantaneous.
  const duration = (v: unknown) =>
    typeof v === "number" && Number.isFinite(v) && v >= 0
      ? Math.min(Math.round(v), 600_000)
      : 0;
  return {
    kind: r.kind as TelemetryKind,
    operation: r.operation as Operation,
    build:
      typeof r.build === "string" &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(r.build)
        ? r.build
        : "development",
    errorType: errorTypes.includes(r.errorType as (typeof errorTypes)[number])
      ? String(r.errorType)
      : "Unknown",
    frames: Array.isArray(r.frames)
      ? r.frames
          .filter(
            (v): v is string =>
              typeof v === "string" &&
              /^[\w-]{1,100}\.js:\d{1,7}:\d{1,7}$/.test(v),
          )
          .slice(0, 6)
      : [],
    status: integer(r.status, 4999),
    durationMs: duration(r.durationMs),
    requestId:
      typeof r.requestId === "string" &&
      /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/.test(
        r.requestId,
      )
        ? r.requestId
        : "",
  };
}
export function fingerprint(event: TelemetryEvent): string {
  const value = [
    event.kind,
    event.operation,
    event.build,
    event.errorType,
    event.status,
    ...event.frames,
  ].join("|");
  let hash = 2166136261;
  for (const ch of value) hash = Math.imul(hash ^ ch.charCodeAt(0), 16777619);
  return (hash >>> 0).toString(16).padStart(8, "0");
}
export function telemetryLog(event: TelemetryEvent, environment: string) {
  return {
    event: "client_error",
    environment,
    fingerprint: fingerprint(event),
    ...event,
  };
}
